import { JOB_TYPES, JOB_TYPE_LABELS } from '@lib/enums/job-type.enum'
import { UNIDENTIFIED_LABEL } from '@lib/enums/tree-mark.enum'
import type { TreeSpeciesDto } from '@lib/dto/tree-species.dto'
import { JobBadge } from '@/field-map/JobBadge'
import { TreeGlyph } from '@/field-map/TreeGlyph'
import { cn } from '@/lib/utils'

/**
 * The same two legends the printed maps carry, on the screen that produces
 * them.
 *
 * Worth having in the field rather than only on the deliverable: the person
 * surveying has to recognize their own pins to avoid double-recording a tree,
 * and a dozen shapes are learnable but not on the first morning.
 *
 * Lists only the species that can currently be chosen, plus "not identified".
 * A legend is for decoding the pins on THIS map, and a retired species nobody
 * can pick is a row somebody has to read past every time.
 */
export function MapLegend({
  species,
  className,
}: {
  species: TreeSpeciesDto[]
  className?: string
}) {
  return (
    <div
      className={cn(
        'w-52 rounded-lg border border-default bg-surface/95 p-2.5 shadow-menu backdrop-blur-none',
        className,
      )}
    >
      <p className="mb-1.5 text-xs font-semibold text-secondary-text">Species</p>
      <ul className="mb-3 space-y-1">
        {species
          .filter((entry) => entry.archivedAt === null)
          .map((entry) => (
            <li key={entry.id} className="flex items-center gap-2 text-xs">
              <TreeGlyph
                species={{
                  name: entry.name,
                  shape: entry.shape,
                  color: entry.color,
                }}
                size={14}
              />
              <span className="truncate">{entry.name}</span>
            </li>
          ))}
        <li className="flex items-center gap-2 text-xs">
          <TreeGlyph species={null} size={14} />
          <span className="truncate">{UNIDENTIFIED_LABEL}</span>
        </li>
      </ul>

      <p className="mb-1.5 text-xs font-semibold text-secondary-text">
        Pruning job types
      </p>
      <ul className="space-y-1">
        {JOB_TYPES.map((type) => (
          <li key={type} className="flex items-center gap-2 text-xs">
            {/* Big enough here for the word on the band to be readable — this
                is where someone comes to decode a pin. */}
            <JobBadge type={type} level={1} height={26} showWord />
            <span className="truncate">{JOB_TYPE_LABELS[type]}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 border-t border-default pt-1.5 text-[11px] leading-snug text-muted-text">
        Badges are Level 1 unless a level is shown.
      </p>
    </div>
  )
}
